import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useRouteMatch, useHistory } from "react-router-dom";
import LoadingPage from "shared/LoadingPage";
import styles from "./index.module.css";
import SortModal from "./SortModal";
import { FilterColumn } from "../../../shared/FilterColumn";
import { Plus } from "react-ikonate";
import { ContentRow } from "./ContentRow";
import Button from "shared/Button";
import SearchInput from "shared/@forms/SearchInput";
import { CMSData } from "../useCMSData";
import { Auth } from "shared/@auth";
import { useToggle } from "react-use";
import { ReactComponent as Plus_Square } from "../icons/Plus_Square.svg";
import Pagination from "shared/Pagination";

const ITEMS_PER_PAGE = 69;

export function PureLanding({
  path,
  history,
  isLoading,
  isError,
  filters,
  selectedFilters,
  setSelectedFilters,
  sortBy,
  setSortBy,
  setSearchTerm,
  searchTerm,
  filteredItems,
  archiveContent,
  hasPermission,
}) {
  const [showSortModal, toggleSortModal] = useToggle(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, searchTerm, sortBy]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const onSearch = (value) => {
    setSearchTerm(value);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className={styles.container}>
      <div className={classNames(styles.searchRow)}>
        <div className={classNames(styles.search)}>
          <SearchInput onSearch={onSearch} value={searchTerm} />
        </div>

        {hasPermission && (
          <div className={styles.button}>
            <Button
              onClick={() => {
                history.push(`${path}/add`);
              }}
            >
              <Plus_Square /> <span>Add New Content</span>
            </Button>
          </div>
        )}

        <Button
          className={styles.filtersToggle}
          aria-expanded={showMobileFilters}
          aria-controls="cmsFiltersPanel"
          onClick={() => setShowMobileFilters((isOpen) => !isOpen)}
        >
          {selectedFilters.length > 0
            ? `Filters (${selectedFilters.length})`
            : "Filters"}
        </Button>

        <Button
          className={styles.btnSort}
          onClick={() => {
            toggleSortModal();
          }}
        >
          <span>Sort</span>
        </Button>
      </div>
      <div className={classNames(styles.row, styles.main)}>
        <div
          id="cmsFiltersPanel"
          className={classNames(styles.col, styles.filters, {
            [styles.filtersHidden]: !showMobileFilters,
          })}
        >
          <FilterColumn
            isHideCollapse={true}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
        </div>

        <div className={classNames(styles.col, styles.content)}>
          <SortModal
            isOpen={showSortModal}
            onClose={toggleSortModal}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          {paginatedItems.map((item) => (
            <ContentRow
              key={item.id}
              item={item}
              hasPermission={hasPermission}
              onEditClicked={() => {
                history.push(`${path}/edit/${item.id}`, {
                  status: item.status,
                });
              }}
              onArchiveClicked={() => {
                archiveContent(item.id, item.status !== 3);
              }}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          ))}

          {totalPages > 1 && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              ariaLabel="CMS content pagination"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { path } = useRouteMatch();
  const history = useHistory();
  const {
    isLoading,
    isError,
    filters,
    selectedFilters,
    setSelectedFilters,
    sortBy,
    setSortBy,
    setSearchTerm,
    searchTerm,
    filteredItems,
    archiveContent,
  } = CMSData.useContainer();
  const { hasPermission } = Auth.useContainer();

  return (
    <PureLanding
      path={path}
      history={history}
      isLoading={isLoading}
      isError={isError}
      filters={filters}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
      sortBy={sortBy}
      setSortBy={setSortBy}
      setSearchTerm={setSearchTerm}
      searchTerm={searchTerm}
      filteredItems={filteredItems}
      archiveContent={archiveContent}
      hasPermission={hasPermission("marketing.cms.write")}
    />
  );
}
